import React, { useEffect, useMemo, useState } from 'react';
import { KTSVG } from '../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth';
import { computeOperationsScopeUx } from '../core/operationsScopeUx';
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage';
import {
    deleteVoyage,
    deleteVoyageReports,
    deleteSingleReport,
    getVoyageReports,
    VoyageReportSummary,
} from '../core/_requests';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    voyageData: any;
    onVoyageDeleted: () => void;
}

const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'N/A';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
            ' ' +
            d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return dateStr;
    }
};

const DeleteVoyageModal: React.FC<Props> = ({ onClose, isOpen, voyageData, onVoyageDeleted }) => {
    const { currentUser } = useAuth();
    const scopeUx = useMemo(() => computeOperationsScopeUx(currentUser, false), [currentUser]);
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState<VoyageReportSummary[]>([]);
    const [busy, setBusy] = useState(false);
    const [deletingReportId, setDeletingReportId] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        const fetchReports = async () => {
            setLoading(true);
            try {
                const data = await getVoyageReports(voyageData.id);
                if (!cancelled) setReports(data);
            } catch (err: any) {
                if (!cancelled) {
                    console.error('Failed to fetch voyage reports:', err);
                    toast.error(getApiErrorMessage(err, 'Failed to load voyage details'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchReports();
        return () => { cancelled = true; };
    }, [voyageData.id]);

    const handleDeleteSingleReport = async (reportId: number) => {
        setDeletingReportId(reportId);
        try {
            await deleteSingleReport(reportId);
            setReports((prev) => prev.filter((r) => r.id !== reportId));
            toast.success('Report deleted');
        } catch (err: any) {
            toast.error(getApiErrorMessage(err, 'Failed to delete report'));
        } finally {
            setDeletingReportId(null);
        }
    };

    const handleDeleteAllReportsAndVoyage = async () => {
        setBusy(true);
        try {
            await deleteVoyageReports(voyageData.id);
            await deleteVoyage(voyageData.id);
            toast.success('All reports and voyage deleted successfully');
            onVoyageDeleted();
            onClose();
        } catch (err: any) {
            toast.error(getApiErrorMessage(err, 'Failed to delete reports and voyage'));
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteVoyageOnly = async () => {
        setBusy(true);
        try {
            await deleteVoyage(voyageData.id);
            toast.success('Voyage deleted successfully');
            onVoyageDeleted();
            onClose();
        } catch (err: any) {
            toast.error(getApiErrorMessage(err, 'Failed to delete voyage'));
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    const hasReports = reports.length > 0;

    return (
        <div className="modal-overlay">
            <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingInline: '1rem', maxWidth: hasReports ? '680px' : '480px' }}
            >
                <div className="custom-modal-header border-0 d-flex justify-content-end align-items-center">
                    <button className="close-btn" onClick={onClose} disabled={busy}>
                        <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
                    </button>
                </div>

                <div className="custom-modal-body pt-0">
                    <div className="d-flex justify-content-center mb-4">
                        <KTSVG path='/media/map/exclamation.svg' className='svg-icon-4x' />
                    </div>

                    {loading ? (
                        <p className="text-center">Loading reports...</p>
                    ) : hasReports ? (
                        <>
                            <p className="text-center fw-bold text-danger mb-2">
                                This voyage has {reports.length} position report(s).
                            </p>
                            <p className="text-center text-muted mb-4" style={{ fontSize: '0.9rem' }}>
                                All reports must be deleted before the voyage can be deleted.
                            </p>

                            {/* Reports table */}
                            <div
                                style={{
                                    maxHeight: '280px',
                                    overflowY: 'auto',
                                    border: '1px solid #e4e6ef',
                                    borderRadius: '6px',
                                }}
                            >
                                <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#f5f8fa', zIndex: 1 }}>
                                        <tr>
                                            <th className="ps-3">#</th>
                                            <th>Report Type</th>
                                            <th>Status</th>
                                            <th>Created Date</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reports.map((report, idx) => (
                                            <tr key={report.id}>
                                                <td className="ps-3">{idx + 1}</td>
                                                <td>{report.reportType || 'N/A'}</td>
                                                <td>
                                                    <span
                                                        className={`badge ${report.status === 'SUBMITTED' ? 'badge-light-success' : 'badge-light-warning'}`}
                                                    >
                                                        {report.status || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>{formatDate(report.createdDateTime)}</td>
                                                {!scopeUx.isCrew && (
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-icon btn-active-light-danger w-25px h-25px"
                                                            title="Delete this report"
                                                            disabled={busy || deletingReportId === report.id}
                                                            onClick={() => handleDeleteSingleReport(report.id)}
                                                        >
                                                            {deletingReportId === report.id ? (
                                                                <span className="spinner-border spinner-border-sm" />
                                                            ) : (
                                                                <KTSVG path='/media/map/trash.svg' className='svg-icon-6' />
                                                            )}
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-center gap-2 mt-5">
                                <button className="btn btn_secondary" onClick={onClose} disabled={busy}>
                                    {scopeUx.isCrew ? 'Close' : 'Cancel'}
                                </button>
                                {!scopeUx.isCrew && (
                                    <button className="btn btn_danger" onClick={handleDeleteAllReportsAndVoyage} disabled={busy}>
                                        {busy ? 'Please wait...' : `Delete All Reports & Voyage`}
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="text-center">
                                Are you sure you want to delete voyage <strong>{voyageData.voyageNumber}</strong>?
                            </p>
                            <div className="d-flex justify-content-center gap-2 mt-5">
                                <button className="btn btn_secondary" onClick={onClose} disabled={busy}>
                                    Cancel
                                </button>
                                <button className="btn btn_danger" onClick={handleDeleteVoyageOnly} disabled={busy}>
                                    {busy ? 'Please wait...' : 'Yes, Delete Voyage'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeleteVoyageModal;

